import {
  Component,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { SidebarItemComponent } from '@modules/common/components/sidebar-item/sidebar-item.component';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { Application } from '@modules/common/types/application';
import { SidebarItem } from '@modules/common/types/sidebar-item';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { Stitch } from '@modules/common/types/stitch';
import { Connection } from '@modules/connections/types/connection';
import { dndStitchPredicate, DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { Knot } from '@modules/knots/types/knot';
import { PopoverService } from '@frontendlib/components/features/popover/services/popover.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { Team } from '@modules/settings/types/team';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { Tag } from '@modules/tags/types/tag';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  template: '',
  standalone: false,
})
export abstract class BaseSidebarComponent<C extends Stitch, I extends Stitch, VirtualFolder extends string>
  implements OnInit, OnDestroy
{
  protected readonly StateKey = StateKey;

  public closeDroppedElement = new Subject<void>();
  public minimized: boolean;
  public currentTeamId: string;

  protected readonly stateService = inject<BaseAppStateService<C, I, VirtualFolder>>(BaseAppStateService);

  /**
   * Declared above the folder list because its `id` comes from the state service: the folder this sidebar
   * offers "All Results" under has to be the one that service selects, or the sidebar would highlight
   * nothing and the user could not get back to where a search had put them. Constraining `VirtualFolder`
   * to `string` is what lets the two be the same value rather than two literals kept in step by hand.
   */
  protected readonly searchResultsItem: SidebarItem = {
    title: 'All results',
    id: this.stateService.searchResultsFolder,
    icon: 'sapphire-search',
  };

  /**
   * The tenant the "All results" row belongs to — always the current one, because a search writes the
   * tenant it ran under before it writes the folder ({@link BaseAppStateService.setTeam} in the search
   * popup's `handleSearch`). Carrying it lets the row reuse the rail's own selection rule instead of a
   * second one, and makes clicking the row rewrite the tenant it already has rather than drop to the
   * personal one.
   */
  protected searchResultsTeam: Team | undefined;
  /** Whether the "All results" row is drawn at all; see {@link handleSidebarItemsUpdates}. */
  protected advancedSearchApplied = false;

  /**
   * How far the root "All results" row pushes every row below it down — 1 while it is drawn, 0
   * otherwise.
   *
   * {@link dndDrop}'s `index` addresses {@link elements}, the rail's `#elRef` children in DOM order,
   * and is what anchors the drop popover for a FOREIGN drag type. Since #1182 round 2c that row is
   * emitted ABOVE every arm, so whenever it is on screen it is `elements[0]` and every list below it
   * starts one further down. This is that arithmetic named ONCE, in the class both arms share,
   * because the round applied it inline to the arm it was looking at and left the other arm pointing
   * one row up (#1212 review F1) — a second inline ternary would only wait to be missed again.
   */
  protected get rootRowOffset(): number {
    return this.advancedSearchApplied ? 1 : 0;
  }

  protected abstract readonly application: Application;
  protected abstract sidebarSplitViewKey: SidebarSplitViewKey;
  protected abstract selfDragDataTypes: DragDataTypes[];
  protected abstract baseItems: SidebarItem[];
  protected items: SidebarItem[];
  protected sidebarFilter: VirtualFolder;
  protected alive = new Subject<void>();
  protected popoverService: PopoverService;

  private splitViewService: SplitViewService;
  private searchService: AdvancedSearchService;

  @ViewChildren('elRef') elements: QueryList<SidebarItemComponent>;
  @ViewChild('afterDropTemplate', { static: true }) template: TemplateRef<void>;

  @Input() team: Team;

  public dndPredicate = (dragData: DragData): boolean => dndStitchPredicate(dragData.type);

  public isDropDisabled = (itemId: string): boolean => {
    return itemId === this.searchResultsItem.id;
  };

  constructor(private injector: Injector) {
    this.splitViewService = injector.get(SplitViewService);
    this.popoverService = injector.get(PopoverService);
    this.searchService = injector.get(AdvancedSearchService);
  }

  /**
   * Lifecycle
   */

  ngOnInit(): void {
    this.splitViewService
      .getMinimized(this.sidebarSplitViewKey)
      .pipe(takeUntil(this.alive))
      .subscribe((minimized) => {
        this.minimized = minimized;
      });

    this.stateService
      .getVirtualFolder()
      .pipe(takeUntil(this.alive))
      .subscribe((folder) => {
        this.sidebarFilter = folder;

        if (!folder) {
          return;
        }

        if (this.searchService.getStateSync()[this.application].containersIds.length === 0) {
          return;
        }

        // Deferred to a microtask: this folder change can itself be a reaction to a search just applied,
        // still inside that search's own in-flight `state.next()` — writing synchronously would nest a
        // second `next()` inside it, and RxJS Subjects aren't reentrant-safe (subscribers not yet reached
        // by the outer dispatch would see the stale, pre-clearing state). `queueMicrotask` over
        // `setTimeout` so it still runs before the next macrotask, and Zone.js patches it like `Promise`
        // so change detection still runs for the state it publishes.
        queueMicrotask(() => {
          // `isStopped`, not `closed`: `ngOnDestroy` only completes `alive`, never unsubscribes it, so
          // `closed` would never flip and this guard would be a permanent no-op.
          if (this.alive.isStopped) {
            return;
          }
          const state = this.searchService.getStateSync();
          if (state[this.application].containersIds.length === 0) {
            return;
          }
          state[this.application].containersIds = [];
          state[this.application].containerName = null;
          this.searchService.setState(state);
        });
      });

    this.stateService
      .getTeam()
      .pipe(takeUntil(this.alive))
      .subscribe((teamId) => {
        this.currentTeamId = teamId;
        this.searchResultsTeam = teamId ? new Team({ id: teamId }) : undefined;
      });

    this.searchService
      .getState()
      .pipe(takeUntil(this.alive))
      .subscribe((state) => {
        if (state[this.application].containersIds.length > 0 && !!this.sidebarFilter) {
          this.stateService.setVirtualFolder(null);
        } else if (state[this.application].containersIds.length === 0 && !this.sidebarFilter) {
          this.stateService.setDefaultVirtualFolder();
        }
      });

    // A search has to land on "All Results", or the hits stay filtered by whichever folder was open and
    // the user sees a fraction of what matched. Owned by the sidebar because the sidebar owns the folder
    // list, and already drops back to the default when the search is cleared.
    //
    // Subscribed 1+N times, not once: MAIN renders this sidebar for the personal context and again for
    // every team, all sharing one state service. **Whatever goes in this handler has to stay idempotent** —
    // it is safe only because `LocalStorageItem.set` returns early on an unchanged value, so N writes of
    // the same folder are one write. A toast, an analytics event or a list refresh added here would fire
    // once per team.
    this.searchService
      .newSearchApplied()
      .pipe(takeUntil(this.alive))
      .subscribe(() => this.stateService.setSearchResultsFolder());

    this.handleSidebarItemsUpdates();
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  selectFilter(filter: VirtualFolder, team: Team | undefined = this.team) {
    const reselected = this.isActiveRow(filter, team);
    this.stateService.setTeam(team);
    this.stateService.setVirtualFolder(filter);
    if (reselected) {
      this.stateService.reselectVirtualFolder();
    }
  }

  /**
   * Whether the row for this folder and tenant is the one drawn as current — the rule every rail's `[active]`
   * binding draws. `null` and `undefined` both mean the personal tenant.
   */
  protected isActiveRow(filter: VirtualFolder, team: Team | undefined): boolean {
    return this.sidebarFilter === filter && (this.currentTeamId ?? null) === (team?.id ?? null);
  }

  dndDrop(dragData: DragData, sidebarFolder: VirtualFolder, index?: number) {
    if (this.selfDragDataTypes.includes(dragData.type)) {
      this.handleMove(dragData, sidebarFolder);
    } else {
      const { elementRef } = this.elements.toArray()[index];

      this.popoverService.create(elementRef, {
        guardChildren: true,
        template: this.template,
        context: { item: this.getStitchFromDragData(dragData, sidebarFolder) },
        placement: 'bottom',
        injector: this.injector,
        trigger: 'click',
        showUntil: this.closeDroppedElement,
      });
    }
  }

  handleClose() {
    this.closeDroppedElement.next();
  }

  abstract handleMore(item: Stitch): void;

  protected abstract handleMove(dragData: DragData, sidebarFolder: VirtualFolder): void;

  protected abstract getStitchFromDragData(dragData: DragData, sidebarFolder: VirtualFolder): Stitch;

  protected getIds(items: (StitchItemSummaryDto | Tag | Knot | Connection)[]): string[] {
    return items.map(({ id }) => id);
  }

  /**
   * Handles the sidebar items updates based on the advanced search applied state.
   * If the advanced search is applied, the "All results" item is added to the list.
   */
  private handleSidebarItemsUpdates(): void {
    this.items = this.baseItems;
    // `merge`, not `combineLatest`, and both values read synchronously inside the handler. The two sources
    // are separate subscriptions to one search state, and the folder changes *because* that state changed,
    // so a cached pair always holds one value from before the change — which is how the reset below used to
    // undo the landing on "All Results". Reading both live makes the handler independent of the order these
    // subscriptions happen to be registered in.
    merge(this.searchService.getState(), this.stateService.getVirtualFolder())
      .pipe(takeUntil(this.alive))
      .subscribe(() => this.renderSidebarItems());
  }

  /** Offers "All results" while anything narrows the app, and never leaves it selected once nothing does. */
  private renderSidebarItems(): void {
    const isAdvancedSearchApplied = this.searchService.getStateSync().isAdvancedSearchApplied(this.application);
    this.advancedSearchApplied = isAdvancedSearchApplied;

    if (isAdvancedSearchApplied) {
      this.items = [this.searchResultsItem, ...this.baseItems];

      return;
    }

    this.items = this.baseItems;
    if (this.stateService.getVirtualFolderSync() === this.searchResultsItem.id) {
      this.stateService.setDefaultVirtualFolder();
    }
  }
}
